import { CSSProperties, ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import TagManager from "react-gtm-module";
import { Conditions, explainConditions, rateConditions } from "../utils/conditions";
import { verdictLine } from "../utils/areaForecast";
import { ConditionsBadge } from "./area/conditionsScore";
import { useRatingsExplainer } from "./ratingsExplainer";
import "./ratingButton.css";

interface RatingButtonProps {
    conditions: Conditions;
    /// What the rating is for, e.g. "Indian Creek · Today" or "Today 4 PM"
    context: string;
    /// A whole day rates on its high temperature, so the details say "High"
    period?: "hour" | "day";
    onDark?: boolean;
    /// Custom look (the label-and-bar meter); defaults to the word badge
    children?: ReactNode;
}

const HOVER_OPEN_MS = 120;
const HOVER_CLOSE_MS = 180;
const GAP = 10;
const EDGE = 12;
const MAX_WIDTH = 320;

/// A climb rating that explains itself: tap (or hover with a mouse) to see what went into it
export function RatingButton({ conditions, context, period = "hour", onDark = false, children }: RatingButtonProps) {
    const rating = rateConditions(conditions);
    const details = explainConditions(conditions, period === "day" ? "High" : "Temperature");
    const explainer = useRatingsExplainer();

    const popoverId = useId();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const timer = useRef<number>();
    const [open, setOpen] = useState(false);
    // Opened by hover closes when the pointer leaves; opened by tap or keyboard stays open
    const [pinned, setPinned] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number; width: number; arrow: number; above: boolean } | null>(null);

    const clearTimer = () => window.clearTimeout(timer.current);

    const show = useCallback((pin: boolean) => {
        clearTimer();
        setPinned(pin);
        setOpen((wasOpen) => {
            if (!wasOpen) {
                TagManager.dataLayer({ dataLayer: { event: "rating_details_open", rating: rating.label, rating_context: context } });
            }
            return true;
        });
    }, [rating.label, context]);

    const hide = useCallback((returnFocus = false) => {
        clearTimer();
        setOpen(false);
        setPinned(false);
        if (returnFocus) {
            buttonRef.current?.focus();
        }
    }, []);

    // Place the popover under the button (or above, if there's no room), kept on screen
    useLayoutEffect(() => {
        if (!open || !buttonRef.current) {
            setPosition(null);
            return;
        }
        const rect = buttonRef.current.getBoundingClientRect();
        const width = Math.min(MAX_WIDTH, window.innerWidth - EDGE * 2);
        const center = rect.left + rect.width / 2;
        const left = Math.min(Math.max(center - width / 2, EDGE), window.innerWidth - width - EDGE);
        const height = popoverRef.current?.offsetHeight ?? 0;
        const above = rect.bottom + GAP + height > window.innerHeight - EDGE && rect.top - GAP - height > EDGE;
        setPosition({
            top: above ? rect.top - GAP - height : rect.bottom + GAP,
            left,
            width,
            arrow: Math.min(Math.max(center - left, 18), width - 18),
            above,
        });
    }, [open]);

    // Close on Escape, a tap elsewhere, scrolling or resizing; leaving focus closes it too
    useEffect(() => {
        if (!open) {
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                hide(true);
            }
        };
        const onPointerDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (!buttonRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
                hide();
            }
        };
        const onMove = () => hide();
        document.addEventListener("keydown", onKey);
        document.addEventListener("pointerdown", onPointerDown);
        window.addEventListener("scroll", onMove, true);
        window.addEventListener("resize", onMove);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("pointerdown", onPointerDown);
            window.removeEventListener("scroll", onMove, true);
            window.removeEventListener("resize", onMove);
        };
    }, [open, hide]);

    useEffect(() => () => clearTimer(), []);

    const onPointerEnter = (e: React.PointerEvent) => {
        if (e.pointerType !== "mouse") {
            return;
        }
        clearTimer();
        timer.current = window.setTimeout(() => show(false), HOVER_OPEN_MS);
    };

    const onPointerLeave = (e: React.PointerEvent) => {
        if (e.pointerType !== "mouse" || pinned) {
            return;
        }
        clearTimer();
        timer.current = window.setTimeout(() => hide(), HOVER_CLOSE_MS);
    };

    const onClick = () => {
        if (open && pinned) {
            hide();
        } else {
            show(true);
        }
    };

    const onPopoverBlur = (e: React.FocusEvent) => {
        // Tabbing out of the popover returns to the button, so keyboard order stays sensible
        const next = e.relatedTarget as Node | null;
        if (next && !popoverRef.current?.contains(next) && next !== buttonRef.current) {
            hide(true);
        }
    };

    const openExplainer = () => {
        hide();
        explainer.open();
    };

    const style = position
        ? ({ top: position.top, left: position.left, width: position.width, "--arrow-x": `${position.arrow}px` } as CSSProperties)
        : { top: 0, left: 0, visibility: "hidden" as const };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className={"cw-rating-button" + (onDark ? " cw-rating-button--on-dark" : "")}
                aria-expanded={open}
                aria-controls={open ? popoverId : undefined}
                aria-label={`${rating.label} climb rating, ${context}. Show details`}
                onClick={onClick}
                onPointerEnter={onPointerEnter}
                onPointerLeave={onPointerLeave}
            >
                {children ?? <ConditionsBadge score={rating.score} onDark={onDark} />}
            </button>
            {open && createPortal(
                <div
                    ref={popoverRef}
                    id={popoverId}
                    role="dialog"
                    aria-label={`${rating.label} climb rating, ${context}`}
                    className={"rating-popover" + (position?.above ? " rating-popover--above" : "")}
                    style={style}
                    onPointerEnter={(e) => e.pointerType === "mouse" && clearTimer()}
                    onPointerLeave={onPointerLeave}
                    onBlur={onPopoverBlur}
                >
                    <div className="rating-popover__header">
                        <ConditionsBadge score={rating.score} />
                        <span className="rating-popover__context">{context}</span>
                    </div>
                    <p className="rating-popover__summary">{verdictLine(rating)}</p>
                    {details.length > 0 && (
                        <ul className="rating-popover__factors">
                            {details.map((d) => (
                                <li key={d.key} className={d.issue ? "rating-popover__factor--issue" : undefined}>
                                    <span className="rating-popover__mark" aria-hidden="true">{d.issue ? "!" : "✓"}</span>
                                    <span className="rating-popover__label">{d.label}</span>
                                    <span className="rating-popover__value">
                                        {d.value}
                                        {d.issue && <span className="cw-sr-only"> (lowered the rating)</span>}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button type="button" className="rating-popover__more" onClick={openExplainer}>
                        How climb ratings work →
                    </button>
                </div>,
                document.body
            )}
        </>
    );
}
